import { portalRequestPath } from './public-request.js';
import {
  AuditIcon,
  GroupsIcon,
  MoonIcon,
  ShieldIcon,
  SunIcon,
  UsersIcon,
} from '@sad5/icons';
import {
  completeActivationSchema,
  createUserSchema,
  type ActivationStatus,
  type AuditEvent,
  type BusinessProfile,
  type BusinessProfileType,
  type CurrentPrincipal,
  type Group,
  type PermissionGrant,
  type PermissionOperationDefinition,
  type ProvisioningResult,
  type PersonAdministrativeNote,
  type UserSummary,
} from '@sad5/contracts';
import {
  Button,
  EmptyState,
  InlineAlert,
  LoadingState,
  Panel,
  StatusBadge,
  TextField,
  useSad5Form,
} from '@sad5/ui';
import { createOnlineReadGuard } from './online-read.js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from 'react-router-dom';

import {
  assignGroupMembership,
  completeActivation,
  createGroup,
  createPermissionGrant,
  createUser,
  downloadUsersCsv,
  explainAccess,
  fetchAudit,
  fetchActivationStatus,
  fetchCurrentPrincipal,
  fetchGroups,
  fetchGroupPermissions,
  fetchPermissionCatalog,
  fetchUser,
  fetchUsers,
  grantProfile,
  logout,
  removeGroupMembership,
  revokePermissionGrant,
  retryProvisioning,
  transitionAccount,
  updateGroup,
  withdrawProfile,
  createPersonAdministrativeNote,
  fetchPersonAdministrativeNotes,
  updatePersonAdministrativeNote,
  updatePersonDescription,
} from './api.js';
import { bindLocalDataToPrincipal, clearLocalData } from './db.js';
import { applyTheme, readTheme, type ThemePreference } from './theme.js';

const stateLabels: Record<UserSummary['state'], string> = {
  active: 'Aktywne',
  anonymized: 'Zanonimizowane',
  archived: 'Archiwalne',
  blocked: 'Zablokowane',
  closed: 'Zamknięte',
  expired: 'Wygasłe',
  invited: 'Zaproszone',
  suspended: 'Zawieszone',
};

const provisioningLabels: Record<UserSummary['provisioningState'], string> = {
  failed: 'Błąd przygotowania',
  pending: 'Oczekuje',
  processing: 'Przygotowywanie',
  ready: 'Gotowe',
};

const profileLabels: Record<
  ProvisioningResult['profiles'][number]['type'],
  string
> = {
  administrator: 'Administrator',
  basic: 'Podstawowy',
  boarding_resident: 'Mieszkaniec internatu',
  candidate: 'Kandydat',
  employee: 'Pracownik',
  guardian: 'Opiekun',
  meal_recipient: 'Osoba żywiona',
  student: 'Uczeń / słuchacz',
  teacher: 'Nauczyciel',
};

const businessProfileTypes = Object.keys(
  profileLabels,
) as BusinessProfileType[];

const PrincipalContext = createContext<CurrentPrincipal | undefined>(undefined);

function stateTone(state: UserSummary['state']) {
  return state === 'active'
    ? 'success'
    : ['blocked', 'closed', 'anonymized'].includes(state)
      ? 'danger'
      : state === 'invited'
        ? 'info'
        : 'neutral';
}

function flattenGroups(groups: Group[]): Group[] {
  return groups.flatMap((group) => [group, ...flattenGroups(group.children)]);
}

function peopleLabel(count: number): string {
  if (count === 1) return '1 osoba';
  if (
    count % 10 >= 2 &&
    count % 10 <= 4 &&
    (count % 100 < 12 || count % 100 > 14)
  )
    return `${count} osoby`;
  return `${count} osób`;
}

export function App() {
  const location = useLocation();
  if (location.pathname === '/activate')
    return (
      <PublicShell>
        <ActivationPage />
      </PublicShell>
    );
  return (
    <AppShell>
      <Routes>
        <Route element={<Navigate replace to="/users" />} path="/" />
        <Route
          element={
            <ProtectedPage operation="iam.users.read">
              <UsersPage />
            </ProtectedPage>
          }
          path="/users"
        />
        <Route
          element={
            <ProtectedPage operation="iam.users.read">
              <UserPage />
            </ProtectedPage>
          }
          path="/users/:userId"
        />
        <Route
          element={
            <ProtectedPage operation="iam.groups.read">
              <GroupsPage />
            </ProtectedPage>
          }
          path="/groups"
        />
        <Route
          element={
            <ProtectedPage operation="iam.access.explain">
              <AccessPage />
            </ProtectedPage>
          }
          path="/access"
        />
        <Route
          element={
            <ProtectedPage operation="iam.audit.read">
              <AuditPage />
            </ProtectedPage>
          }
          path="/audit"
        />
        <Route element={<Navigate replace to="/users" />} path="*" />
      </Routes>
    </AppShell>
  );
}

function ProtectedPage({
  children,
  operation,
}: {
  children: ReactNode;
  operation: string;
}) {
  const principal = useContext(PrincipalContext);
  if (!principal?.allowedOperations.includes(operation))
    return (
      <InlineAlert title="Brak dostępu" tone="warning">
        To konto nie ma dostępu do tej części systemu.
      </InlineAlert>
    );
  return children;
}

function PublicShell({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemePreference>(readTheme);
  const cycleTheme = () => {
    const next =
      theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark';
    setTheme(next);
    applyTheme(next);
  };
  return (
    <div className="public-shell">
      <header className="public-shell__header">
        <span className="brand">
          <span className="brand__mark">S5</span>
          <span>
            <strong>SAD5</strong>
            <small>Zespół Szkół nr 5</small>
          </span>
        </span>
        <Button
          aria-label={`Motyw: ${theme}`}
          onPress={cycleTheme}
          variant="quiet"
        >
          {theme === 'light' ? <SunIcon /> : <MoonIcon />}
          <span className="wide-only">{theme}</span>
        </Button>
      </header>
      <main className="public-shell__content">{children}</main>
    </div>
  );
}

function AppShell({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemePreference>(readTheme);
  const [online, setOnline] = useState(navigator.onLine);
  const [principalReady, setPrincipalReady] = useState(false);
  const [principal, setPrincipal] = useState<CurrentPrincipal>();

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  useEffect(() => {
    void fetchCurrentPrincipal()
      .then(async (current) => {
        await bindLocalDataToPrincipal();
        setPrincipal(current);
      })
      .catch(async () => {
        await clearLocalData();
      })
      .finally(() => setPrincipalReady(true));
  }, []);

  const cycleTheme = () => {
    const next =
      theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark';
    setTheme(next);
    applyTheme(next);
  };

  if (!principalReady)
    return (
      <div className="app-bootstrap">
        <LoadingState label="Przygotowuję bezpieczny profil lokalny" />
      </div>
    );

  const can = (operation: string) =>
    principal?.allowedOperations.includes(operation) ?? false;
  const initials =
    principal?.displayName
      .split(' ')
      .map((part) => part[0])
      .slice(0, 2)
      .join('') ?? '—';

  return (
    <PrincipalContext.Provider value={principal}>
      <div className="app-shell">
        <header className="topbar">
          <NavLink aria-label="SAD5 — strona główna" className="brand" to="/">
            <span className="brand__mark">S5</span>
            <span>
              <strong>SAD5</strong>
              <small>Zespół Szkół nr 5</small>
            </span>
          </NavLink>
          <div className="topbar__actions">
            <StatusBadge tone={online ? 'success' : 'warning'}>
              {online ? 'Online' : 'Offline'}
            </StatusBadge>
            <Button
              aria-label={`Motyw: ${theme}`}
              onPress={cycleTheme}
              variant="quiet"
            >
              {theme === 'light' ? <SunIcon /> : <MoonIcon />}
              <span className="wide-only">{theme}</span>
            </Button>
            <button
              aria-label={`Wyloguj ${principal?.displayName ?? 'użytkownika'}`}
              className="avatar"
              onClick={() => {
                void clearLocalData()
                  .then(logout)
                  .then(({ globalLogoutUrl }) =>
                    window.location.assign(globalLogoutUrl),
                  );
              }}
              title={`Wyloguj ${principal?.displayName ?? 'użytkownika'}`}
            >
              {initials}
            </button>
          </div>
        </header>
        <aside className="sidebar" aria-label="Nawigacja główna">
          {can('iam.users.read') ? (
            <NavItem icon={<UsersIcon />} label="Użytkownicy" to="/users" />
          ) : null}
          {can('iam.groups.read') ? (
            <NavItem icon={<GroupsIcon />} label="Grupy" to="/groups" />
          ) : null}
          {can('iam.access.explain') ? (
            <NavItem icon={<ShieldIcon />} label="Uprawnienia" to="/access" />
          ) : null}
          {can('iam.audit.read') ? (
            <NavItem icon={<AuditIcon />} label="Audyt" to="/audit" />
          ) : null}
        </aside>
        <main className="main-content">{children}</main>
        <nav aria-label="Nawigacja mobilna" className="mobile-nav">
          {can('iam.users.read') ? (
            <NavItem icon={<UsersIcon />} label="Osoby" to="/users" />
          ) : null}
          {can('iam.groups.read') ? (
            <NavItem icon={<GroupsIcon />} label="Grupy" to="/groups" />
          ) : null}
          {can('iam.access.explain') ? (
            <NavItem icon={<ShieldIcon />} label="Prawa" to="/access" />
          ) : null}
          {can('iam.audit.read') ? (
            <NavItem icon={<AuditIcon />} label="Audyt" to="/audit" />
          ) : null}
        </nav>
      </div>
    </PrincipalContext.Provider>
  );
}

function NavItem({
  icon,
  label,
  to,
}: {
  icon: ReactNode;
  label: string;
  to: string;
}) {
  return (
    <NavLink
      className={({ isActive }) =>
        `nav-item${isActive ? ' nav-item--active' : ''}`
      }
      to={to}
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

function PageHeading({
  actions,
  eyebrow,
  title,
}: {
  actions?: ReactNode;
  eyebrow: string;
  title: string;
}) {
  return (
    <div className="page-heading">
      <div>
        <p>{eyebrow}</p>
        <h1>{title}</h1>
      </div>
      {actions}
    </div>
  );
}

function UsersPage() {
  const navigate = useNavigate();
  const principal = useContext(PrincipalContext);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<UserSummary['state']>();
  const [profileFilter, setProfileFilter] = useState<BusinessProfileType>();
  const [groupFilter, setGroupFilter] = useState<string>();
  const [filterGroups, setFilterGroups] = useState<Group[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const directoryRead = useRef(createOnlineReadGuard());
  useEffect(() => {
    const clear = () => {
      directoryRead.current.begin();
      setUsers([]);
      setFilterGroups([]);
      setNextOffset(null);
      setTotal(0);
      setLoading(false);
    };
    window.addEventListener('offline', clear);
    return () => {
      window.removeEventListener('offline', clear);
      directoryRead.current.begin();
    };
  }, []);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [exporting, setExporting] = useState(false);
  const loadPage = useCallback(
    async (offset = 0) => {
      const requestId = directoryRead.current.begin();
      setLoading(true);
      setUsers([]);
      try {
        const page = await fetchUsers({
          search: searchQuery,
          state: stateFilter,
          profileType: profileFilter,
          groupId: groupFilter,
          offset,
          limit: 100,
        });
        if (!directoryRead.current.isCurrent(requestId)) return;
        setUsers(page.items);
        setPageOffset(offset);
        setNextOffset(page.nextOffset);
        setTotal(page.total);
        setError(undefined);
      } catch (reason) {
        if (!directoryRead.current.isCurrent(requestId)) return;
        setUsers([]);
        setNextOffset(null);
        setTotal(0);
        setError(
          reason instanceof Error
            ? reason.message
            : 'Nie udało się pobrać użytkowników',
        );
      } finally {
        if (directoryRead.current.isCurrent(requestId)) setLoading(false);
      }
    },
    [groupFilter, profileFilter, searchQuery, stateFilter],
  );

  useEffect(() => {
    setLoading(true);
    void loadPage();
    return () => {
      directoryRead.current.begin();
    };
  }, [loadPage]);
  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    void fetchGroups()
      .then(setFilterGroups)
      .catch(() => undefined);
  }, []);
  const filtered = useMemo(() => {
    return users.filter((user) =>
      `${user.displayName} ${user.email ?? ''} ${user.description ?? ''}`
        .toLocaleLowerCase('pl')
        .includes(search.toLocaleLowerCase('pl')),
    );
  }, [search, users]);
  const activeFilterCount = [stateFilter, profileFilter, groupFilter].filter(
    Boolean,
  ).length;

  return (
    <>
      <PageHeading
        actions={
          <div className="button-row">
            <Button
              isDisabled={exporting}
              onPress={() => {
                setExporting(true);
                setError(undefined);
                void downloadUsersCsv({
                  search: searchQuery,
                  state: stateFilter,
                  profileType: profileFilter,
                  groupId: groupFilter,
                })
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `sad5-users-${new Date().toISOString().slice(0, 10)}.csv`;
                    link.click();
                    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
                  })
                  .catch((reason: unknown) =>
                    setError(
                      reason instanceof Error
                        ? reason.message
                        : 'Błąd eksportu',
                    ),
                  )
                  .finally(() => setExporting(false));
              }}
              variant="secondary"
            >
              {exporting ? 'Eksportuję…' : 'Eksport CSV'}
            </Button>
            <Button onPress={() => void navigate('/users/new')}>
              Dodaj użytkownika
            </Button>
          </div>
        }
        eyebrow="Tożsamość i dostęp"
        title="Użytkownicy"
      />
      <div className="toolbar">
        <TextField
          aria-label="Szukaj użytkownika"
          label="Szukaj"
          onChange={setSearch}
          placeholder={
            users.some((user) => user.redactedFields.includes('email')) ||
            !principal?.allowedOperations.includes('iam.users.contact.read')
              ? 'Nazwisko, imię lub e-mail'
              : 'Nazwisko lub imię'
          }
          value={search}
        />
        <div className="summary">
          <strong>{total || filtered.length}</strong>
          <span>widocznych kont</span>
        </div>
      </div>
      <div className="filter-toolbar">
        <Button
          onPress={() => setFiltersOpen((open) => !open)}
          variant="secondary"
        >
          Filtry{activeFilterCount ? ` (${activeFilterCount})` : ''}
        </Button>
        {activeFilterCount ? (
          <Button
            onPress={() => {
              setStateFilter(undefined);
              setProfileFilter(undefined);
              setGroupFilter(undefined);
            }}
            variant="quiet"
          >
            Wyczyść filtry
          </Button>
        ) : null}
      </div>
      {filtersOpen ? (
        <Panel className="filter-panel">
          <h3>Stan konta</h3>
          <div className="button-row">
            {(
              [
                'active',
                'invited',
                'blocked',
                'suspended',
                'expired',
                'closed',
              ] as const
            ).map((state) => (
              <Button
                aria-pressed={stateFilter === state}
                key={state}
                onPress={() =>
                  setStateFilter((current) =>
                    current === state ? undefined : state,
                  )
                }
                variant={stateFilter === state ? 'primary' : 'secondary'}
              >
                {stateLabels[state]}
              </Button>
            ))}
          </div>
          <h3>Profil</h3>
          <div className="button-row">
            {businessProfileTypes.map((profile) => (
              <Button
                aria-pressed={profileFilter === profile}
                key={profile}
                onPress={() =>
                  setProfileFilter((current) =>
                    current === profile ? undefined : profile,
                  )
                }
                variant={profileFilter === profile ? 'primary' : 'secondary'}
              >
                {profileLabels[profile]}
              </Button>
            ))}
          </div>
          <h3>Grupa</h3>
          <div className="button-row">
            {flattenGroups(filterGroups).map((group) => (
              <Button
                aria-pressed={groupFilter === group.id}
                key={group.id}
                onPress={() =>
                  setGroupFilter((current) =>
                    current === group.id ? undefined : group.id,
                  )
                }
                variant={groupFilter === group.id ? 'primary' : 'secondary'}
              >
                {group.name}
              </Button>
            ))}
          </div>
        </Panel>
      ) : null}
      {error ? (
        <InlineAlert title="Pracujesz na danych lokalnych" tone="warning">
          {error}
        </InlineAlert>
      ) : null}
      <Panel className="list-panel">
        {loading && users.length === 0 ? (
          <LoadingState label="Synchronizuję użytkowników" />
        ) : filtered.length === 0 ? (
          <EmptyState
            description="Zmień wyszukiwanie albo dodaj pierwszego użytkownika."
            title="Brak pasujących osób"
          />
        ) : (
          <div className="user-list" role="list">
            {filtered.map((user) => (
              <button
                className="user-row"
                key={user.id}
                onClick={() => void navigate(`/users/${user.id}`)}
                role="listitem"
              >
                <span className="person-avatar">
                  {user.displayName
                    .split(' ')
                    .map((part) => part[0])
                    .slice(0, 2)
                    .join('')}
                </span>
                <span className="user-row__identity">
                  <strong>{user.displayName}</strong>
                  <small>{user.email ?? 'Adres ukryty'}</small>
                  {user.description ? <small>{user.description}</small> : null}
                </span>
                <span className="user-row__meta">
                  <StatusBadge tone={stateTone(user.state)}>
                    {stateLabels[user.state]}
                  </StatusBadge>
                  {user.provisioningState !== 'ready' ? (
                    <StatusBadge
                      tone={
                        user.provisioningState === 'failed'
                          ? 'danger'
                          : 'warning'
                      }
                    >
                      {provisioningLabels[user.provisioningState]}
                    </StatusBadge>
                  ) : null}
                  <small>{user.groupIds.length} grup</small>
                </span>
                <span aria-hidden="true" className="row-arrow">
                  ›
                </span>
              </button>
            ))}
          </div>
        )}
        {pageOffset > 0 || nextOffset !== null ? (
          <div className="load-more">
            {pageOffset > 0 ? (
              <Button
                isDisabled={loading}
                onPress={() => void loadPage(Math.max(0, pageOffset - 100))}
                variant="secondary"
              >
                Poprzednia strona
              </Button>
            ) : null}
            {nextOffset !== null ? (
              <Button
                isDisabled={loading}
                onPress={() => void loadPage(nextOffset)}
                variant="secondary"
              >
                Następna strona
              </Button>
            ) : null}
          </div>
        ) : null}
      </Panel>
    </>
  );
}

function UserPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const principal = useContext(PrincipalContext);
  const [user, setUser] = useState<Awaited<ReturnType<typeof fetchUser>>>();
  const [error, setError] = useState<string>();
  const [blocking, setBlocking] = useState(false);
  const [transitionReason, setTransitionReason] = useState('');
  const [pendingTransition, setPendingTransition] = useState<{
    label: string;
    targetState: 'active' | 'blocked' | 'suspended' | 'expired' | 'closed';
  }>();
  const [retrying, setRetrying] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [addingProfile, setAddingProfile] = useState(false);
  const [profileReason, setProfileReason] = useState('');
  const [pendingProfileWithdrawal, setPendingProfileWithdrawal] =
    useState<BusinessProfile>();
  const [institutionGroups, setInstitutionGroups] = useState<Group[]>([]);
  const [addingMembership, setAddingMembership] = useState(false);
  const [membershipBusy, setMembershipBusy] = useState(false);
  const [membershipReason, setMembershipReason] = useState('');
  const [pendingGroupRemoval, setPendingGroupRemoval] = useState<Group>();
  const [newActivation, setNewActivation] =
    useState<ProvisioningResult['activation']>();
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState<PersonAdministrativeNote[]>([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [notesBusy, setNotesBusy] = useState(false);
  const [editingNote, setEditingNote] = useState<PersonAdministrativeNote>();
  const [editingNoteBody, setEditingNoteBody] = useState('');
  const userLoadSequence = useRef(createOnlineReadGuard());
  useEffect(() => {
    const clear = () => {
      userLoadSequence.current.begin();
      setUser(undefined);
      setNotes([]);
      setDescription('');
      setEditingNote(undefined);
    };
    window.addEventListener('offline', clear);
    return () => {
      window.removeEventListener('offline', clear);
      userLoadSequence.current.begin();
    };
  }, []);
  useEffect(() => {
    const requestSequence = userLoadSequence.current.begin();
    setUser(undefined);
    setDescription('');
    setError(undefined);
    setNotes([]);
    setEditingNote(undefined);
    if (userId && userId !== 'new')
      void fetchUser(userId)
        .then(async (loaded) => {
          if (!userLoadSequence.current.isCurrent(requestSequence)) return;
          setUser(loaded);
          setDescription(loaded.description ?? '');
          if (principal?.allowedOperations.includes('iam.personnotes.read')) {
            const loadedNotes = await fetchPersonAdministrativeNotes(
              loaded.personId,
            );
            if (userLoadSequence.current.isCurrent(requestSequence))
              setNotes(loadedNotes);
          }
        })
        .catch((reason: unknown) => {
          if (userLoadSequence.current.isCurrent(requestSequence)) {
            setUser(undefined);
            setNotes([]);
            setDescription('');
            setEditingNote(undefined);
            setEditingNoteBody('');
            setError(reason instanceof Error ? reason.message : 'Błąd');
          }
        });
    return () => {
      userLoadSequence.current.begin();
    };
  }, [userId, principal?.allowedOperations]);
  useEffect(() => {
    void fetchGroups()
      .then(setInstitutionGroups)
      .catch(() => undefined);
  }, []);
  if (userId === 'new')
    return (
      <CreateUserForm
        onCancel={() => void navigate('/users')}
        onCreated={(id) => void navigate(`/users/${id}`)}
      />
    );
  if (error)
    return (
      <InlineAlert title="Nie udało się otworzyć konta" tone="danger">
        {error}
      </InlineAlert>
    );
  if (!user || user.id !== userId) return <LoadingState />;
  const chooseTransition = (
    targetState: 'active' | 'blocked' | 'suspended' | 'expired' | 'closed',
    label: string,
  ) => {
    setTransitionReason('');
    setPendingTransition({ targetState, label });
  };
  const executeTransition = () => {
    if (!pendingTransition || transitionReason.trim().length < 3) return;
    setBlocking(true);
    void transitionAccount(user.id, {
      targetState: pendingTransition.targetState,
      reason: transitionReason.trim(),
    })
      .then((result) => {
        setUser(result);
        setPendingTransition(undefined);
        setTransitionReason('');
      })
      .catch((reasonValue: unknown) =>
        setError(reasonValue instanceof Error ? reasonValue.message : 'Błąd'),
      )
      .finally(() => setBlocking(false));
  };
  const executeProfileGrant = (type: BusinessProfileType) => {
    setProfileBusy(true);
    setError(undefined);
    void grantProfile(user.id, { type })
      .then((result) => {
        setUser(result);
        setAddingProfile(false);
      })
      .catch((reasonValue: unknown) =>
        setError(reasonValue instanceof Error ? reasonValue.message : 'Błąd'),
      )
      .finally(() => setProfileBusy(false));
  };
  const executeProfileWithdrawal = () => {
    if (!pendingProfileWithdrawal || profileReason.trim().length < 3) return;
    setProfileBusy(true);
    setError(undefined);
    void withdrawProfile(user.id, pendingProfileWithdrawal.id, {
      reason: profileReason.trim(),
    })
      .then((result) => {
        setUser(result);
        setPendingProfileWithdrawal(undefined);
        setProfileReason('');
      })
      .catch((reasonValue: unknown) =>
        setError(reasonValue instanceof Error ? reasonValue.message : 'Błąd'),
      )
      .finally(() => setProfileBusy(false));
  };
  const activeProfiles = user.profiles.filter(
    (profile) => profile.status === 'active',
  );
  const availableProfileTypes = businessProfileTypes.filter(
    (type) => !activeProfiles.some((profile) => profile.type === type),
  );
  const flatInstitutionGroups = flattenGroups(institutionGroups);
  const assignedGroups = flatInstitutionGroups.filter((group) =>
    user.groupIds.includes(group.id),
  );
  const unassignedGroups = flatInstitutionGroups.filter(
    (group) => !user.groupIds.includes(group.id),
  );
  const mappedGroupNames = new Set(
    activeProfiles.flatMap((profile) =>
      profile.type === 'administrator'
        ? ['Administracja']
        : profile.type === 'teacher'
          ? ['Nauczyciele']
          : profile.type === 'guardian'
            ? ['Rodzice']
            : [],
    ),
  );
  const executeMembershipAssignment = (groupId: string) => {
    setMembershipBusy(true);
    setError(undefined);
    void assignGroupMembership(user.id, {
      groupId,
      validFrom: null,
      validUntil: null,
    })
      .then((result) => {
        setUser(result);
        setAddingMembership(false);
      })
      .catch((reasonValue: unknown) =>
        setError(reasonValue instanceof Error ? reasonValue.message : 'Błąd'),
      )
      .finally(() => setMembershipBusy(false));
  };
  const executeMembershipRemoval = () => {
    if (!pendingGroupRemoval || membershipReason.trim().length < 3) return;
    setMembershipBusy(true);
    setError(undefined);
    void removeGroupMembership(
      user.id,
      pendingGroupRemoval.id,
      membershipReason.trim(),
    )
      .then((result) => {
        setUser(result);
        setPendingGroupRemoval(undefined);
        setMembershipReason('');
      })
      .catch((reasonValue: unknown) =>
        setError(reasonValue instanceof Error ? reasonValue.message : 'Błąd'),
      )
      .finally(() => setMembershipBusy(false));
  };
  const saveDescription = () => {
    if (!principal?.allowedOperations.includes('iam.users.write')) return;
    setNotesBusy(true);
    void updatePersonDescription(user.personId, {
      description: description.trim() || null,
      revision: user.revision,
    })
      .then((result) => setUser({ ...user, ...result }))
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      )
      .finally(() => setNotesBusy(false));
  };
  const addNote = () => {
    if (!noteDraft.trim()) return;
    setNotesBusy(true);
    void createPersonAdministrativeNote(user.personId, {
      body: noteDraft.trim(),
    })
      .then((note) => {
        setNotes((current) => [note, ...current]);
        setNoteDraft('');
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      )
      .finally(() => setNotesBusy(false));
  };
  const saveNoteEdit = () => {
    if (!editingNote || !editingNoteBody.trim()) return;
    setNotesBusy(true);
    void updatePersonAdministrativeNote(user.personId, editingNote.id, {
      body: editingNoteBody.trim(),
      revision: editingNote.revision,
    })
      .then((updated) => {
        setNotes((current) =>
          current.map((note) => (note.id === updated.id ? updated : note)),
        );
        setEditingNote(undefined);
        setEditingNoteBody('');
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      )
      .finally(() => setNotesBusy(false));
  };
  return (
    <>
      <PageHeading
        actions={
          <StatusBadge tone={stateTone(user.state)}>
            {stateLabels[user.state]}
          </StatusBadge>
        }
        eyebrow="Konto użytkownika"
        title={user.displayName}
      />
      <div className="detail-layout">
        <Panel className="detail-card">
          <h2>Dane podstawowe</h2>
          <dl className="details">
            <div>
              <dt>E-mail</dt>
              <dd>{user.email ?? 'Dane ukryte'}</dd>
            </div>
            <div>
              <dt>Język</dt>
              <dd>{user.locale}</dd>
            </div>
            <div>
              <dt>Ostatnie logowanie</dt>
              <dd>
                {user.redactedFields.includes('lastLoginAt')
                  ? 'Dane ukryte'
                  : user.lastLoginAt
                    ? new Date(user.lastLoginAt).toLocaleString('pl-PL')
                    : 'Jeszcze nigdy'}
              </dd>
            </div>
            <div>
              <dt>Wersja danych</dt>
              <dd>{user.revision}</dd>
            </div>
            <div>
              <dt>Przygotowanie konta</dt>
              <dd>{provisioningLabels[user.provisioningState]}</dd>
            </div>
          </dl>
          <h3>Opis osoby</h3>
          {principal?.allowedOperations.includes('iam.users.write') ? (
            <>
              <textarea
                aria-label="Opis osoby"
                maxLength={200}
                onChange={(event) => setDescription(event.target.value)}
                value={description}
              />
              <div className="form-actions">
                <Button
                  isDisabled={notesBusy}
                  onPress={saveDescription}
                  variant="secondary"
                >
                  Zapisz opis
                </Button>
                <small>{description.length}/200</small>
              </div>
            </>
          ) : (
            <p>{user.description || 'Brak opisu.'}</p>
          )}
          {principal?.allowedOperations.includes('iam.personnotes.read') ? (
            <section className="profile-editor">
              <h3>Prywatne notatki administracyjne</h3>
              {notes.length ? (
                notes.map((note) => (
                  <article className="profile-row" key={note.id}>
                    <div>
                      <strong>{note.authorDisplayName}</strong>
                      <small>
                        {new Date(note.createdAt).toLocaleString('pl-PL')} ·
                        wersja {note.revision}
                      </small>
                      <p>{note.body}</p>
                      {note.history.length > 1 ? (
                        <details>
                          <summary>
                            Historia zmian ({note.history.length})
                          </summary>
                          {note.history.map((item) => (
                            <div key={item.revision}>
                              <small>
                                Wersja {item.revision} ·{' '}
                                {item.editedByDisplayName} ·{' '}
                                {new Date(item.editedAt).toLocaleString(
                                  'pl-PL',
                                )}
                              </small>
                              <p>{item.body}</p>
                            </div>
                          ))}
                        </details>
                      ) : null}
                    </div>
                    {principal?.allowedOperations.includes(
                      'iam.personnotes.edit',
                    ) ? (
                      <Button
                        isDisabled={notesBusy}
                        onPress={() => {
                          setEditingNote(note);
                          setEditingNoteBody(note.body);
                        }}
                        variant="quiet"
                      >
                        Edytuj
                      </Button>
                    ) : null}
                  </article>
                ))
              ) : (
                <p className="muted">Brak prywatnych notatek.</p>
              )}
              {editingNote ? (
                <div className="form-stack">
                  <textarea
                    aria-label="Edycja prywatnej notatki"
                    maxLength={2000}
                    onChange={(event) => setEditingNoteBody(event.target.value)}
                    value={editingNoteBody}
                  />
                  <div className="form-actions">
                    <Button
                      onPress={() => setEditingNote(undefined)}
                      variant="quiet"
                    >
                      Anuluj
                    </Button>
                    <Button
                      isDisabled={notesBusy || !editingNoteBody.trim()}
                      onPress={saveNoteEdit}
                      variant="secondary"
                    >
                      Zapisz nową wersję
                    </Button>
                  </div>
                </div>
              ) : null}
              {principal?.allowedOperations.includes(
                'iam.personnotes.create',
              ) ? (
                <>
                  <textarea
                    aria-label="Nowa prywatna notatka"
                    maxLength={2000}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    value={noteDraft}
                  />
                  <div className="form-actions">
                    <Button
                      isDisabled={notesBusy || !noteDraft.trim()}
                      onPress={addNote}
                      variant="secondary"
                    >
                      Dodaj notatkę
                    </Button>
                    <small>{noteDraft.length}/2000</small>
                  </div>
                </>
              ) : null}
            </section>
          ) : null}
          <h3>Profile</h3>
          <div className="profile-list">
            {user.profiles.map((profile) => (
              <div className="profile-row" key={profile.id}>
                <StatusBadge
                  tone={profile.status === 'active' ? 'info' : 'neutral'}
                >
                  {profileLabels[profile.type]}
                  {profile.status === 'withdrawn' ? ' · wycofany' : ''}
                </StatusBadge>
                {profile.status === 'active' && activeProfiles.length > 1 ? (
                  <Button
                    isDisabled={profileBusy}
                    onPress={() => {
                      setProfileReason('');
                      setPendingProfileWithdrawal(profile);
                    }}
                    variant="quiet"
                  >
                    Wycofaj
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
          {addingProfile ? (
            <div className="form-stack profile-editor">
              <p className="muted">Wybierz rolę osoby w placówce.</p>
              <div className="button-row">
                {availableProfileTypes.map((type) => (
                  <Button
                    isDisabled={profileBusy}
                    key={type}
                    onPress={() => executeProfileGrant(type)}
                    variant="secondary"
                  >
                    {profileLabels[type]}
                  </Button>
                ))}
              </div>
              <Button onPress={() => setAddingProfile(false)} variant="quiet">
                Anuluj
              </Button>
            </div>
          ) : availableProfileTypes.length ? (
            <Button
              isDisabled={profileBusy}
              onPress={() => setAddingProfile(true)}
              variant="secondary"
            >
              Dodaj profil
            </Button>
          ) : null}
          {pendingProfileWithdrawal ? (
            <div className="form-stack profile-editor">
              <InlineAlert title="Wycofaj profil" tone="warning">
                Historia profilu zostanie zachowana w audycie.
              </InlineAlert>
              <TextField
                label="Powód wycofania"
                onChange={setProfileReason}
                placeholder="Krótko opisz przyczynę"
                value={profileReason}
              />
              <div className="form-actions">
                <Button
                  onPress={() => setPendingProfileWithdrawal(undefined)}
                  variant="secondary"
                >
                  Anuluj
                </Button>
                <Button
                  isDisabled={profileBusy || profileReason.trim().length < 3}
                  onPress={executeProfileWithdrawal}
                  variant="danger"
                >
                  {profileBusy ? 'Zapisuję…' : 'Wycofaj profil'}
                </Button>
              </div>
            </div>
          ) : null}
          {user.provisioningError ? (
            <InlineAlert title="Przygotowanie wymaga uwagi" tone="danger">
              {user.provisioningError}
            </InlineAlert>
          ) : null}
          {newActivation ? (
            <InlineAlert title="Ponowne zaproszenie jest gotowe" tone="success">
              Kod awaryjny: <strong>{newActivation.adminCode}</strong>. Ważny do{' '}
              {new Date(newActivation.expiresAt).toLocaleString('pl-PL')}.
            </InlineAlert>
          ) : null}
          {user.state === 'invited' && user.provisioningState === 'failed' ? (
            <Button
              isDisabled={retrying}
              onPress={() => {
                setRetrying(true);
                setError(undefined);
                void retryProvisioning(user.id)
                  .then((result) => {
                    setUser(result);
                    setNewActivation(result.activation);
                  })
                  .catch((reason: unknown) =>
                    setError(reason instanceof Error ? reason.message : 'Błąd'),
                  )
                  .finally(() => setRetrying(false));
              }}
              variant="secondary"
            >
              {retrying ? 'Ponawiam…' : 'Ponów przygotowanie'}
            </Button>
          ) : null}
        </Panel>
        <Panel className="detail-card">
          <h2>Dostęp</h2>
          <h3>Grupy</h3>
          <div className="profile-list">
            {assignedGroups.length ? (
              assignedGroups.map((group) => (
                <div className="profile-row" key={group.id}>
                  <StatusBadge tone="neutral">{group.name}</StatusBadge>
                  {principal?.allowedOperations.includes('iam.groups.write') &&
                  !mappedGroupNames.has(group.name) ? (
                    <Button
                      isDisabled={membershipBusy}
                      onPress={() => {
                        setMembershipReason('');
                        setPendingGroupRemoval(group);
                      }}
                      variant="quiet"
                    >
                      Usuń
                    </Button>
                  ) : null}
                </div>
              ))
            ) : (
              <span className="muted">Brak przypisania do grup.</span>
            )}
          </div>
          {principal?.allowedOperations.includes('iam.groups.write') ? (
            addingMembership ? (
              <div className="form-stack profile-editor">
                <p className="muted">Wybierz grupę.</p>
                <div className="button-row">
                  {unassignedGroups.map((group) => (
                    <Button
                      isDisabled={membershipBusy}
                      key={group.id}
                      onPress={() => executeMembershipAssignment(group.id)}
                      variant="secondary"
                    >
                      {group.name}
                    </Button>
                  ))}
                </div>
                <Button
                  onPress={() => setAddingMembership(false)}
                  variant="quiet"
                >
                  Anuluj
                </Button>
              </div>
            ) : unassignedGroups.length ? (
              <Button
                isDisabled={membershipBusy}
                onPress={() => setAddingMembership(true)}
                variant="secondary"
              >
                Dodaj do grupy
              </Button>
            ) : null
          ) : null}
          {pendingGroupRemoval ? (
            <div className="form-stack profile-editor">
              <InlineAlert title="Usuń z grupy" tone="warning">
                Zmiana wpłynie na uprawnienia użytkownika.
              </InlineAlert>
              <TextField
                label="Powód usunięcia"
                onChange={setMembershipReason}
                placeholder="Krótko opisz przyczynę"
                value={membershipReason}
              />
              <div className="form-actions">
                <Button
                  onPress={() => setPendingGroupRemoval(undefined)}
                  variant="secondary"
                >
                  Anuluj
                </Button>
                <Button
                  isDisabled={
                    membershipBusy || membershipReason.trim().length < 3
                  }
                  onPress={executeMembershipRemoval}
                  variant="danger"
                >
                  {membershipBusy ? 'Zapisuję…' : 'Usuń z grupy'}
                </Button>
              </div>
            </div>
          ) : null}
          <p className="muted">
            Użytkownik należy do {user.groupIds.length} grup. Ostateczny dostęp
            wylicza wspólny evaluator reguł.
          </p>
          <div className="button-row">
            <Button
              onPress={() =>
                void navigate(
                  `/access?accountId=${encodeURIComponent(user.id)}&operation=iam.users.read`,
                )
              }
              variant="secondary"
            >
              Wyjaśnij dostęp
            </Button>
            {user.id !== principal?.accountId && user.state === 'active' ? (
              <Button
                isDisabled={blocking}
                onPress={() => chooseTransition('suspended', 'Zawieś konto')}
                variant="secondary"
              >
                Zawieś
              </Button>
            ) : null}
            {user.id !== principal?.accountId &&
            ['blocked', 'suspended', 'expired'].includes(user.state) ? (
              <Button
                isDisabled={blocking}
                onPress={() => chooseTransition('active', 'Przywróć konto')}
                variant="secondary"
              >
                Przywróć
              </Button>
            ) : null}
            {user.id !== principal?.accountId &&
            !['closed', 'anonymized', 'archived'].includes(user.state) ? (
              <Button
                isDisabled={blocking}
                onPress={() => chooseTransition('closed', 'Zamknij konto')}
                variant="danger"
              >
                {blocking ? 'Zapisuję…' : 'Zamknij konto'}
              </Button>
            ) : null}
          </div>
          {pendingTransition ? (
            <div className="form-stack">
              <InlineAlert title={pendingTransition.label} tone="warning">
                Zmiana zostanie zapisana w audycie i może odwołać aktywne sesje.
              </InlineAlert>
              <TextField
                label="Powód zmiany"
                onChange={setTransitionReason}
                placeholder="Krótko opisz przyczynę"
                value={transitionReason}
              />
              <div className="form-actions">
                <Button
                  onPress={() => setPendingTransition(undefined)}
                  variant="secondary"
                >
                  Anuluj
                </Button>
                <Button
                  isDisabled={blocking || transitionReason.trim().length < 3}
                  onPress={executeTransition}
                  variant={
                    pendingTransition.targetState === 'closed'
                      ? 'danger'
                      : 'primary'
                  }
                >
                  {blocking ? 'Zapisuję…' : 'Potwierdź zmianę'}
                </Button>
              </div>
            </div>
          ) : null}
        </Panel>
      </div>
    </>
  );
}

function CreateUserForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [error, setError] = useState<string>();
  const [created, setCreated] = useState<ProvisioningResult>();
  const form = useSad5Form({
    defaultValues: {
      givenName: '',
      familyName: '',
      email: '',
      locale: 'pl-PL',
      groupIds: [] as string[],
    },
    validators: {
      onSubmit: ({ value }) =>
        createUserSchema.safeParse(value).success
          ? undefined
          : 'Uzupełnij poprawnie wymagane pola.',
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await createUser(createUserSchema.parse(value));
        setCreated(result);
      } catch (reason) {
        setError(
          reason instanceof Error
            ? reason.message
            : 'Nie udało się utworzyć użytkownika',
        );
      }
    },
  });
  if (created)
    return (
      <>
        <PageHeading eyebrow="Konto przygotowane" title={created.displayName} />
        <Panel className="detail-card">
          <InlineAlert title="Zaproszenie zapisane" tone="success">
            E-mail aktywacyjny jest dostarczany przez worker. Awaryjny kod
            administratora pokaż użytkownikowi innym kanałem.
          </InlineAlert>
          <dl className="details">
            <div>
              <dt>Awaryjny kod aktywacji</dt>
              <dd>
                <code>{created.activation.adminCode}</code>
              </dd>
            </div>
            <div>
              <dt>Ważny do</dt>
              <dd>
                {new Date(created.activation.expiresAt).toLocaleString('pl-PL')}
              </dd>
            </div>
          </dl>
          <div className="form-actions">
            <Button onPress={() => onCreated(created.id)}>Otwórz konto</Button>
          </div>
        </Panel>
      </>
    );
  return (
    <>
      <PageHeading eyebrow="Nowe konto" title="Dodaj użytkownika" />
      <Panel className="detail-card">
        <InlineAlert title="Bezpieczna aktywacja" tone="info">
          Konto otrzyma jednorazowy link e-mail. Ponowienie żądania nie utworzy
          duplikatu.
        </InlineAlert>
        {error ? (
          <InlineAlert title="Nie udało się zapisać" tone="danger">
            {error}
          </InlineAlert>
        ) : null}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <div className="form-grid">
            <form.Field name="givenName">
              {(field) => (
                <TextField
                  isInvalid={field.state.meta.errors.length > 0}
                  label="Imię"
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  value={field.state.value}
                />
              )}
            </form.Field>
            <form.Field name="familyName">
              {(field) => (
                <TextField
                  isInvalid={field.state.meta.errors.length > 0}
                  label="Nazwisko"
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  value={field.state.value}
                />
              )}
            </form.Field>
            <form.Field name="email">
              {(field) => (
                <TextField
                  description="Na ten adres wyślemy jednorazową aktywację."
                  isInvalid={field.state.meta.errors.length > 0}
                  label="Adres e-mail"
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={field.handleChange}
                  type="email"
                  value={field.state.value}
                />
              )}
            </form.Field>
          </div>
          <div className="form-actions">
            <Button onPress={onCancel} type="button" variant="secondary">
              Anuluj
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  isDisabled={Boolean(!canSubmit || isSubmitting)}
                  type="submit"
                >
                  {isSubmitting ? 'Tworzę konto…' : 'Zapisz i wyślij aktywację'}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </Panel>
    </>
  );
}

function ActivationPage() {
  const [parameters] = useSearchParams();
  const initialToken = parameters.get('token') ?? '';
  const [token, setToken] = useState(initialToken);
  const [code, setCode] = useState(initialToken);
  const [status, setStatus] = useState<ActivationStatus>();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus(undefined);
      return;
    }
    void fetchActivationStatus(token)
      .then(setStatus)
      .catch(() => setStatus({ valid: false }));
  }, [token]);

  if (!token)
    return (
      <>
        <PageHeading eyebrow="Awaryjna aktywacja" title="Wpisz kod aktywacji" />
        <Panel className="detail-card">
          <p className="muted">
            Kod otrzymasz od administratora po potwierdzeniu tożsamości. Jest
            jednorazowy i ma krótki termin ważności.
          </p>
          <TextField
            label="Kod aktywacji"
            onChange={(value) => setCode(value.toUpperCase())}
            value={code}
          />
          <div className="form-actions">
            <Button
              isDisabled={code.trim().length < 8}
              onPress={() => setToken(code.trim().toUpperCase())}
            >
              Sprawdź kod
            </Button>
          </div>
        </Panel>
      </>
    );
  if (!status) return <LoadingState label="Sprawdzam aktywację" />;
  if (!status.valid)
    return (
      <>
        <InlineAlert title="Aktywacja niedostępna" tone="warning">
          Link lub kod jest nieprawidłowy, wykorzystany albo wygasł.
        </InlineAlert>
        <div className="form-actions">
          <Button
            onPress={() => {
              setCode('');
              setToken('');
            }}
            variant="secondary"
          >
            Wpisz inny kod
          </Button>
        </div>
      </>
    );
  if (done)
    return (
      <>
        <PageHeading eyebrow="Aktywacja zakończona" title="Konto jest gotowe" />
        <Panel className="detail-card">
          <InlineAlert title="Możesz się zalogować" tone="success">
            Przy pierwszym logowaniu skonfigurujesz dodatkowe zabezpieczenie
            TOTP.
          </InlineAlert>
          <div className="form-actions">
            <Button
              onPress={() =>
                window.location.assign(portalRequestPath('/auth/login'))
              }
            >
              Przejdź do logowania
            </Button>
          </div>
        </Panel>
      </>
    );

  return (
    <>
      <PageHeading
        eyebrow="Jednorazowa aktywacja"
        title={`Witaj${status.displayName ? `, ${status.displayName}` : ''}`}
      />
      <Panel className="detail-card">
        <p className="muted">
          Ustaw silne hasło. Musi mieć co najmniej 12 znaków, małą i wielką
          literę, cyfrę oraz znak specjalny.
        </p>
        {error ? (
          <InlineAlert title="Nie udało się aktywować konta" tone="danger">
            {error}
          </InlineAlert>
        ) : null}
        <div className="form-grid">
          <TextField
            label="Nowe hasło"
            onChange={setPassword}
            type="password"
            value={password}
          />
          <TextField
            label="Powtórz hasło"
            onChange={setPasswordConfirmation}
            type="password"
            value={passwordConfirmation}
          />
        </div>
        <div className="form-actions">
          <Button
            isDisabled={submitting}
            onPress={() => {
              const parsed = completeActivationSchema.safeParse({
                token,
                password,
                passwordConfirmation,
              });
              if (!parsed.success) {
                setError('Hasło nie spełnia wymagań albo wartości są różne.');
                return;
              }
              setSubmitting(true);
              setError(undefined);
              void completeActivation(parsed.data)
                .then(() => setDone(true))
                .catch((reason: unknown) =>
                  setError(
                    reason instanceof Error
                      ? reason.message
                      : 'Aktywacja nie powiodła się',
                  ),
                )
                .finally(() => setSubmitting(false));
            }}
          >
            {submitting ? 'Aktywuję…' : 'Aktywuj konto'}
          </Button>
        </div>
      </Panel>
    </>
  );
}

function GroupsPage() {
  const principal = useContext(PrincipalContext);
  const [groups, setGroups] = useState<Group[]>([]);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Group>();
  const [editorOpen, setEditorOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mfaTrustHours, setMfaTrustHours] = useState('720');
  const [parentId, setParentId] = useState<string | null>(null);
  const [permissionGroup, setPermissionGroup] = useState<Group>();
  const [permissions, setPermissions] = useState<PermissionGrant[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<
    PermissionOperationDefinition[]
  >([]);
  const [permissionBusy, setPermissionBusy] = useState(false);
  const [addingPermission, setAddingPermission] = useState(false);
  const [permissionOperation, setPermissionOperation] = useState('');
  const [permissionEffect, setPermissionEffect] = useState<'allow' | 'deny'>(
    'allow',
  );
  const [pendingPermissionRevoke, setPendingPermissionRevoke] =
    useState<PermissionGrant>();
  const [permissionReason, setPermissionReason] = useState('');
  const reload = useCallback(() => {
    void fetchGroups()
      .then(setGroups)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      );
  }, []);
  useEffect(() => {
    reload();
  }, [reload]);
  useEffect(() => {
    void fetchPermissionCatalog()
      .then(setPermissionCatalog)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      );
  }, []);
  const openEditor = (
    group?: Group,
    proposedParentId: string | null = null,
  ) => {
    setEditing(group);
    setName(group?.name ?? '');
    setDescription(group?.description ?? '');
    setMfaTrustHours(String(group?.mfaTrustHours ?? 720));
    setParentId(group?.parentId ?? proposedParentId);
    setEditorOpen(true);
    setError(undefined);
  };
  const save = () => {
    const hours = Number(mfaTrustHours);
    if (!name.trim() || !Number.isInteger(hours) || hours < 0 || hours > 8760)
      return;
    setSaving(true);
    const operation = editing
      ? updateGroup(editing.id, {
          revision: editing.revision,
          name: name.trim(),
          description: description.trim() || null,
          parentId,
          mfaTrustHours: hours,
        })
      : createGroup({
          name: name.trim(),
          description: description.trim() || null,
          parentId,
          mfaTrustHours: hours,
        });
    void operation
      .then(() => {
        setEditorOpen(false);
        setEditing(undefined);
        reload();
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      )
      .finally(() => setSaving(false));
  };
  const openPermissions = (group: Group) => {
    setPermissionGroup(group);
    setAddingPermission(false);
    setPendingPermissionRevoke(undefined);
    setPermissionBusy(true);
    setError(undefined);
    void fetchGroupPermissions(group.id)
      .then(setPermissions)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      )
      .finally(() => setPermissionBusy(false));
  };
  const savePermission = () => {
    if (!permissionGroup || !permissionOperation.trim()) return;
    setPermissionBusy(true);
    setError(undefined);
    void createPermissionGrant(permissionGroup.id, {
      operation: permissionOperation.trim(),
      effect: permissionEffect,
      scope: {},
      validFrom: null,
      validUntil: null,
    })
      .then((result) => {
        setPermissions((current) => [result, ...current]);
        setAddingPermission(false);
        setPermissionOperation('');
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      )
      .finally(() => setPermissionBusy(false));
  };
  const executePermissionRevoke = () => {
    if (
      !permissionGroup ||
      !pendingPermissionRevoke ||
      permissionReason.trim().length < 3
    )
      return;
    setPermissionBusy(true);
    setError(undefined);
    void revokePermissionGrant(
      permissionGroup.id,
      pendingPermissionRevoke.id,
      pendingPermissionRevoke.revision,
      permissionReason.trim(),
    )
      .then((result) => {
        setPermissions((current) =>
          current.map((permission) =>
            permission.id === result.id ? result : permission,
          ),
        );
        setPendingPermissionRevoke(undefined);
        setPermissionReason('');
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      )
      .finally(() => setPermissionBusy(false));
  };
  const canWrite = principal?.allowedOperations.includes('iam.groups.write');
  const canReadPermissions = principal?.allowedOperations.includes(
    'iam.permissions.read',
  );
  const canWritePermissions = principal?.allowedOperations.includes(
    'iam.permissions.write',
  );
  const permissionCatalogByModule = useMemo(
    () =>
      Array.from(
        permissionCatalog.reduce((modules, operation) => {
          const current = modules.get(operation.module) ?? {
            label: operation.moduleLabel,
            operations: [] as PermissionOperationDefinition[],
          };
          current.operations.push(operation);
          modules.set(operation.module, current);
          return modules;
        }, new Map<string, { label: string; operations: PermissionOperationDefinition[] }>()),
      ),
    [permissionCatalog],
  );
  const parentName = parentId
    ? flattenGroups(groups).find((group) => group.id === parentId)?.name
    : undefined;
  return (
    <>
      <PageHeading
        actions={
          canWrite ? (
            <Button onPress={() => openEditor()}>Dodaj grupę</Button>
          ) : undefined
        }
        eyebrow="Struktura placówki"
        title="Grupy i podgrupy"
      />
      {error ? (
        <InlineAlert title="Nie udało się wykonać operacji" tone="danger">
          {error}
        </InlineAlert>
      ) : null}
      {editorOpen ? (
        <Panel className="detail-card group-editor">
          <h2>{editing ? 'Edytuj grupę' : 'Nowa grupa'}</h2>
          <div className="form-grid">
            <TextField label="Nazwa" onChange={setName} value={name} />
            <TextField
              label="Czas zaufania MFA (godziny)"
              onChange={setMfaTrustHours}
              type="number"
              value={mfaTrustHours}
            />
            <TextField
              label="Opis"
              onChange={setDescription}
              value={description}
            />
          </div>
          <p className="muted">
            Położenie:{' '}
            {parentName ? `pod grupą „${parentName}”` : 'poziom główny'}
          </p>
          <div className="form-actions">
            <Button onPress={() => setEditorOpen(false)} variant="secondary">
              Anuluj
            </Button>
            <Button
              isDisabled={
                saving ||
                !name.trim() ||
                !Number.isInteger(Number(mfaTrustHours))
              }
              onPress={save}
            >
              {saving ? 'Zapisuję…' : 'Zapisz grupę'}
            </Button>
          </div>
        </Panel>
      ) : null}
      {permissionGroup ? (
        <Panel className="detail-card group-editor">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Reguły dostępu</p>
              <h2>{permissionGroup.name}</h2>
            </div>
            <Button
              onPress={() => setPermissionGroup(undefined)}
              variant="quiet"
            >
              Zamknij
            </Button>
          </div>
          {permissionBusy && !permissions.length ? <LoadingState /> : null}
          <div className="permission-list">
            {permissions.map((permission) => {
              const operation = permissionCatalog.find(
                (item) => item.id === permission.operation,
              );
              return (
                <div className="permission-row" key={permission.id}>
                  <span>
                    <strong>{operation?.label ?? permission.operation}</strong>
                    <small>
                      {operation
                        ? `${operation.moduleLabel} · ${operation.id}`
                        : `Nieznana operacja historyczna · ${permission.operation}`}
                      <br />
                      {permission.effect === 'deny' ? 'Odmów' : 'Zezwól'} ·{' '}
                      {permission.status === 'active' ? 'aktywna' : 'wycofana'}
                    </small>
                  </span>
                  <StatusBadge
                    tone={
                      permission.status === 'revoked'
                        ? 'neutral'
                        : permission.effect === 'deny'
                          ? 'danger'
                          : 'success'
                    }
                  >
                    {permission.effect === 'deny' ? 'Odmów' : 'Zezwól'}
                  </StatusBadge>
                  {canWritePermissions && permission.status === 'active' ? (
                    <Button
                      isDisabled={permissionBusy}
                      onPress={() => {
                        setPermissionReason('');
                        setPendingPermissionRevoke(permission);
                      }}
                      variant="quiet"
                    >
                      Wycofaj
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
          {canWritePermissions ? (
            addingPermission ? (
              <div className="form-stack profile-editor">
                <label className="catalog-select">
                  Operacja
                  <select
                    aria-label="Operacja"
                    onChange={(event) =>
                      setPermissionOperation(event.target.value)
                    }
                    value={permissionOperation}
                  >
                    <option value="">Wybierz operację…</option>
                    {permissionCatalogByModule.map(([module, definition]) => (
                      <optgroup key={module} label={definition.label}>
                        {definition.operations.map((operation) => (
                          <option key={operation.id} value={operation.id}>
                            {operation.label} — {operation.id}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <small>
                    Katalog zawiera tylko operacje egzekwowane przez aktywne
                    moduły.
                  </small>
                </label>
                <div
                  className="button-row"
                  role="group"
                  aria-label="Efekt reguły"
                >
                  <Button
                    aria-pressed={permissionEffect === 'allow'}
                    onPress={() => setPermissionEffect('allow')}
                    variant={
                      permissionEffect === 'allow' ? 'primary' : 'secondary'
                    }
                  >
                    Zezwól
                  </Button>
                  <Button
                    aria-pressed={permissionEffect === 'deny'}
                    onPress={() => setPermissionEffect('deny')}
                    variant={
                      permissionEffect === 'deny' ? 'danger' : 'secondary'
                    }
                  >
                    Odmów
                  </Button>
                </div>
                <div className="form-actions">
                  <Button
                    onPress={() => setAddingPermission(false)}
                    variant="secondary"
                  >
                    Anuluj
                  </Button>
                  <Button
                    isDisabled={permissionBusy || !permissionOperation.trim()}
                    onPress={savePermission}
                  >
                    Dodaj regułę
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onPress={() => {
                  setPermissionOperation(permissionCatalog[0]?.id ?? '');
                  setAddingPermission(true);
                }}
                variant="secondary"
              >
                Dodaj regułę
              </Button>
            )
          ) : null}
          {pendingPermissionRevoke ? (
            <div className="form-stack profile-editor">
              <InlineAlert title="Wycofaj regułę" tone="warning">
                Historia pozostanie w systemie, ale reguła przestanie działać.
              </InlineAlert>
              <TextField
                label="Powód wycofania"
                onChange={setPermissionReason}
                value={permissionReason}
              />
              <div className="form-actions">
                <Button
                  onPress={() => setPendingPermissionRevoke(undefined)}
                  variant="secondary"
                >
                  Anuluj
                </Button>
                <Button
                  isDisabled={
                    permissionBusy || permissionReason.trim().length < 3
                  }
                  onPress={executePermissionRevoke}
                  variant="danger"
                >
                  Wycofaj regułę
                </Button>
              </div>
            </div>
          ) : null}
        </Panel>
      ) : null}
      <Panel className="tree-panel">
        {groups.length === 0 && !error ? (
          <LoadingState />
        ) : (
          <GroupTree
            canWrite={Boolean(canWrite)}
            canReadPermissions={Boolean(canReadPermissions)}
            groups={groups}
            onAddChild={(group) => openEditor(undefined, group.id)}
            onEdit={(group) => openEditor(group)}
            onPermissions={openPermissions}
          />
        )}
      </Panel>
    </>
  );
}

function GroupTree({
  canWrite,
  canReadPermissions,
  groups,
  level = 1,
  onAddChild,
  onEdit,
  onPermissions,
}: {
  canWrite: boolean;
  canReadPermissions: boolean;
  groups: Group[];
  level?: number;
  onAddChild: (group: Group) => void;
  onEdit: (group: Group) => void;
  onPermissions: (group: Group) => void;
}) {
  return (
    <ul
      aria-label={level === 1 ? 'Grupy placówki' : undefined}
      className="group-tree"
      role={level === 1 ? 'tree' : 'group'}
    >
      {groups.map((group) => (
        <li
          aria-expanded={group.children.length ? true : undefined}
          aria-level={level}
          key={group.id}
          role="treeitem"
        >
          <div className="group-node">
            <GroupsIcon />
            <span>
              <strong>{group.name}</strong>
              <small>
                {peopleLabel(group.memberCount)} · MFA{' '}
                {group.mfaTrustHours === 0
                  ? 'przy każdym logowaniu'
                  : `${group.mfaTrustHours} h`}
              </small>
            </span>
            {canWrite || canReadPermissions ? (
              <span className="group-actions">
                {canWrite ? (
                  <>
                    <Button onPress={() => onAddChild(group)} variant="quiet">
                      Dodaj podgrupę
                    </Button>
                    <Button onPress={() => onEdit(group)} variant="quiet">
                      Edytuj
                    </Button>
                  </>
                ) : null}
                {canReadPermissions ? (
                  <Button onPress={() => onPermissions(group)} variant="quiet">
                    Prawa
                  </Button>
                ) : null}
              </span>
            ) : null}
          </div>
          {group.children.length ? (
            <GroupTree
              canWrite={canWrite}
              canReadPermissions={canReadPermissions}
              groups={group.children}
              level={level + 1}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onPermissions={onPermissions}
            />
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function AccessPage() {
  const [parameters] = useSearchParams();
  const [accountId, setAccountId] = useState(parameters.get('accountId') ?? '');
  const [operation, setOperation] = useState(
    parameters.get('operation') ?? 'iam.users.read',
  );
  const [result, setResult] =
    useState<Awaited<ReturnType<typeof explainAccess>>>();
  const [error, setError] = useState<string>();
  const [permissionCatalog, setPermissionCatalog] = useState<
    PermissionOperationDefinition[]
  >([]);
  useEffect(() => {
    void fetchPermissionCatalog()
      .then(setPermissionCatalog)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      );
  }, []);
  return (
    <>
      <PageHeading
        eyebrow="Uprawnienia"
        title="Katalog i wyjaśnienie dostępu"
      />
      <Panel className="detail-card">
        <h2>Katalog operacji</h2>
        <p className="muted">
          Tylko poniższe operacje można nadać grupie. Moduł jest właścicielem
          opisu zakresu, a IAM sprawdza katalog przed zapisaniem reguły.
        </p>
        <div className="permission-list" aria-label="Katalog operacji">
          {permissionCatalog.map((item) => (
            <div className="permission-row" key={item.id}>
              <span>
                <strong>{item.label}</strong>
                <small>
                  {item.moduleLabel} · {item.id}
                  <br />
                  {item.description}
                </small>
              </span>
            </div>
          ))}
        </div>
      </Panel>
      <Panel className="detail-card">
        <h2>Wyjaśnienie dostępu</h2>
        <p className="muted">
          Wybierz osobę i operację. System pokaże grupy, reguły, odmowy oraz
          zakres, które doprowadziły do decyzji.
        </p>
        <div className="form-grid">
          <TextField
            label="Identyfikator konta"
            onChange={setAccountId}
            placeholder="UUID użytkownika"
            value={accountId}
          />
          <label className="catalog-select">
            Operacja
            <select
              aria-label="Operacja do wyjaśnienia"
              onChange={(event) => setOperation(event.target.value)}
              value={operation}
            >
              {permissionCatalog.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.moduleLabel} · {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-actions">
          <Button
            onPress={() => {
              setError(undefined);
              void explainAccess(accountId, operation)
                .then(setResult)
                .catch((reason: unknown) =>
                  setError(reason instanceof Error ? reason.message : 'Błąd'),
                );
            }}
          >
            Sprawdź dostęp
          </Button>
        </div>
        {error ? (
          <InlineAlert title="Nie można wyjaśnić dostępu" tone="danger">
            {error}
          </InlineAlert>
        ) : null}
        {result ? (
          <InlineAlert
            title={result.allowed ? 'Dostęp przyznany' : 'Dostęp zabroniony'}
            tone={result.allowed ? 'success' : 'warning'}
          >
            {result.reason} Sprawdzono {result.evaluatedGroupIds.length} grup.
          </InlineAlert>
        ) : null}
      </Panel>
    </>
  );
}

function AuditPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string>();
  useEffect(() => {
    void fetchAudit()
      .then(setEvents)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Błąd'),
      );
  }, []);
  return (
    <>
      <PageHeading eyebrow="Bezpieczeństwo" title="Dziennik audytu" />
      {error ? (
        <InlineAlert title="Nie udało się pobrać audytu" tone="danger">
          {error}
        </InlineAlert>
      ) : null}
      <Panel className="audit-panel">
        {events.length === 0 && !error ? (
          <LoadingState />
        ) : (
          <ol className="audit-list">
            {events.map((event) => (
              <li key={event.id}>
                <span className={`audit-dot audit-dot--${event.outcome}`} />
                <span>
                  <strong>{event.summary}</strong>
                  <small>
                    {new Date(event.occurredAt).toLocaleString('pl-PL')} ·{' '}
                    {event.correlationId}
                  </small>
                </span>
                <StatusBadge
                  tone={
                    event.outcome === 'success'
                      ? 'success'
                      : event.outcome === 'denied'
                        ? 'warning'
                        : 'danger'
                  }
                >
                  {event.outcome}
                </StatusBadge>
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </>
  );
}
